import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getSupplierSelectList } from '../../clients/supplier_client';
import { getOrderContactSettings } from '../../clients/order_request_client';
import globalSupplierSelectCache from '../../config_and_helpers/supplier_cache';
import { showToast } from '../../config_and_helpers/helpers';
import GlobalOrderContactModal from '../OrderRequest/GlobalOrderContactModal';

import { Spinner } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import SearchIcon from '@mui/icons-material/Search';
import Form from 'react-bootstrap/Form';
import RefreshIcon from '@mui/icons-material/Refresh';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import InfiniteScroll from 'react-infinite-scroller';
import {
  clearAllOrderNotifications,
  getOrderNotifications,
} from '../../clients/notifications_client';
import NotificationsTable from './NotificationsTable';
import {
  logListSearchEvent,
  logListSearchError,
  logInfo,
} from '../../config_and_helpers/logger';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  resetPaginationForPartialSearch,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';

const LOG_CONTEXT = 'NotificationsList';

/**
 * Paginated order notifications list with search, supplier filter, and bulk reset.
 */
const NotificationsList = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const isLoadingRef = useRef(false);
  const isMountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);
  const hasMoreRef = useRef(true);

  const [notifications, setNotifications] = useState([]);
  const [supplierSelectList, setSupplierSelectList] = useState([]);
  const [supplier, setSupplier] = useState('');
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);
  const [isLoadingContactSettings, setIsLoadingContactSettings] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  const hasContactEmail = Boolean(contactSettings?.contact_email);

  const loadContactSettings = useCallback(async () => {
    if (!token) return;
    setIsLoadingContactSettings(true);
    const response = await getOrderContactSettings(token);
    setIsLoadingContactSettings(false);
    if (response?.success) {
      setContactSettings(response.data);
      logInfo(LOG_CONTEXT, 'contact settings loaded', {
        hasEmail: Boolean(response.data?.contact_email),
      });
    }
  }, [token]);

  useEffect(() => {
    loadContactSettings();
  }, [loadContactSettings]);

  useEffect(() => {
    const cacheIsValid =
      globalSupplierSelectCache.data &&
      (!globalSupplierSelectCache.invalidatedAt ||
        !globalSupplierSelectCache.timestamp ||
        globalSupplierSelectCache.timestamp >
          globalSupplierSelectCache.invalidatedAt);

    if (cacheIsValid) {
      setSupplierSelectList(globalSupplierSelectCache.data);
      return;
    }

    if (
      globalSupplierSelectCache.isFetching &&
      globalSupplierSelectCache.promise
    ) {
      globalSupplierSelectCache.promise.then(() => {
        if (globalSupplierSelectCache.data) {
          setSupplierSelectList(globalSupplierSelectCache.data);
        }
      });
      return;
    }

    globalSupplierSelectCache.isFetching = true;
    globalSupplierSelectCache.promise = getSupplierSelectList(
      token,
      data => {
        globalSupplierSelectCache.data = data;
        globalSupplierSelectCache.timestamp = Date.now();
        globalSupplierSelectCache.isFetching = false;
        setSupplierSelectList(data);
      },
      'order_notifications'
    );

    globalSupplierSelectCache.promise.then(() => {
      globalSupplierSelectCache.isFetching = false;
    });
  }, [token]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
    };
  }, []);

  const fetchNotifications = useCallback(
    (
      { searchValue = '', supplierId = '', nextPage = null } = {},
      newSearch = false
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const params = {
        searchInput: searchValue,
        supplierId,
        nextPage,
        view: 'order',
      };

      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }

      isLoadingRef.current = true;
      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        setNotifications([]);
      }

      const controller = new AbortController();
      currentRequestRef.current = controller;

      const notificationPromise = getOrderNotifications(token, setNotifications, {
        searchInput: searchValue,
        supplierId,
        nextPage,
        signal: controller.signal,
      });

      logListSearchEvent('notifications', 'request', {
        trigger,
        params,
      });

      notificationPromise
        .then(response => {
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          const responseMeta = {
            success: response && response.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (response && response.success) {
            if (response.reachedEnd) {
              setNextPageUrl(null);
              setHasMore(false);
              hasMoreRef.current = false;
            } else {
              setNextPageUrl(response.nextPage);
              setHasMore(true);
              hasMoreRef.current = true;
            }
          } else {
            showToast(
              'An unexpected error occurred. Please try again',
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            'notifications',
            response && response.success ? 'success' : 'failure',
            {
              trigger,
              params,
              responseMeta,
            }
          );
          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError('notifications', trigger, error, { params });

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        });
    },
    [token]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    if (!isRunnableListSearchQuery(searchInput)) {
      return;
    }

    fetchNotifications(
      {
        supplierId: supplier,
        searchValue: searchInput,
        nextPage: null,
      },
      true
    );
  }, [token, supplier, searchInput, fetchNotifications]);

  const resetSearchValue = () => {
    setSearchInput('');
  };

  const refreshList = () =>
    fetchNotifications(
      {
        supplierId: supplier,
        searchValue: searchInput,
        nextPage: null,
      },
      true
    );

  const handleDeleteSuccess = notificationId => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== notificationId)
    );
    logInfo(LOG_CONTEXT, 'notification removed from list after delete', {
      notificationId,
    });
  };

  const handleClearAll = async () => {
    setIsClearingAll(true);
    const response = await clearAllOrderNotifications(token);
    setIsClearingAll(false);

    if (response?.success) {
      setNotifications([]);
      setNextPageUrl(null);
      setHasMore(false);
      setShowClearAllModal(false);
      response.toast();
      logInfo(LOG_CONTEXT, 'clear all notifications success', {
        deletedCount: response.deletedCount,
      });
      return;
    }

    showToast(
      response?.detail || 'Failed to clear reorder alerts. Please try again.',
      'error',
      'top-right',
      5000
    );
  };

  const handleSearchInput = useCallback(value => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (value.length >= 2) {
      setNotifications([]);
      setIsSearching(true);
    }

    if (isRunnableListSearchQuery(value)) {
      debounceTimeoutRef.current = setTimeout(() => {
        setSearchInput(value);
      }, 300);
    } else if (isPartialListSearchQuery(value)) {
      setSearchInput(value);
      setNotifications([]);
      setIsSearching(false);
      resetPaginationForPartialSearch({
        setHasMore,
        hasMoreRef,
        setNextPageUrl,
        abortControllerRef: currentRequestRef,
      });
    }
  }, []);

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      setSearchInput(event.target.value);
    }
  };

  return (
    <div>
      <div className="glass-toolbar">
        {!isLoadingContactSettings && !hasContactEmail ? (
          <Alert variant="warning" className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <span>
              Reorder alerts are in-app only until a lab contact email is set.
            </span>
            {!isViewer ? (
            <Button
              variant="outline-dark"
              size="sm"
              onClick={() => setShowContactModal(true)}
            >
              Set lab contact
            </Button>
            ) : null}
          </Alert>
        ) : null}

        <Row className="align-items-center g-2">
          <Col xs={5} className="ms-2">
            <InputGroup>
              <InputGroup.Text id="basic-addon1">
                <SearchIcon />
              </InputGroup.Text>
              <Form.Control
                size="lg"
                type="text"
                value={searchInput}
                placeholder="Free Search"
                aria-label="Search "
                aria-describedby="basic-addon1"
                onChange={e => handleSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </InputGroup>
          </Col>
          <Col md="auto" sm>
            <InputGroup>
              <Form.Select
                value={supplier}
                onChange={e => {
                  setNextPageUrl(null);
                  setSupplier(e.target.value);
                }}
              >
                <option value="" disabled>
                  -- Select Supplier --
                </option>
                {supplierSelectList.map((choice, index) => (
                  <option key={index} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </Form.Select>
              <InputGroup.Text
                onClick={() => setSupplier('')}
                style={{ cursor: 'pointer' }}
              >
                <RefreshIcon />
              </InputGroup.Text>
            </InputGroup>
          </Col>
          {!isViewer ? (
          <Col md="auto" className="text-end">
            <Button
              variant="outline-danger"
              disabled={!notifications.length || isClearingAll}
              onClick={() => setShowClearAllModal(true)}
            >
              Reset all
            </Button>
          </Col>
          ) : null}
        </Row>
      </div>

      {(isLoading && !notifications.length) || isSearching ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          <div className="mt-2 text-muted">
            {isSearching && searchInput.length >= 2
              ? 'Searching notifications...'
              : 'Loading notifications...'}
          </div>
        </div>
      ) : !notifications.length ? (
        <div className="glass-empty">
          <div className="mb-3">
            <span role="img" aria-label="no notifications" style={{ fontSize: '3rem' }}>
              🔔
            </span>
          </div>
          <h5 className="mb-2">No notifications found</h5>
          <p className="mb-3">
            {searchInput.length >= 2
              ? `No notifications match your search for "${searchInput}"`
              : supplier
                ? 'No reorder alerts for this supplier'
                : 'No reorder alerts right now'}
          </p>
          {searchInput.length >= 2 ? (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={resetSearchValue}
            >
              Clear search
            </button>
          ) : null}
        </div>
      ) : (
      <InfiniteScroll
        pageStart={0}
        loadMore={() => {
          if (
            isLoadingRef.current ||
            !nextPageUrl ||
            isPartialListSearchQuery(searchInput)
          ) {
            return;
          }

          const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
            search: searchInput,
            supplier_id: supplier,
          });
          if (mismatch === null) {
            logListSearchError(
              'notifications',
              'next-page-url-stale-check',
              new Error('Failed to parse nextPageUrl'),
              { nextPageUrl, searchInput, supplier }
            );
            setNextPageUrl(null);
            setHasMore(false);
            hasMoreRef.current = false;
            return;
          }
          if (mismatch.mismatched) {
            logListSearchEvent('notifications', 'next-page-url-stale-mismatch', {
              nextPageUrl,
              expected: mismatch.expected,
              actual: mismatch.actual,
            });
            setNextPageUrl(null);
            setHasMore(false);
            hasMoreRef.current = false;
            return;
          }

          fetchNotifications(
            {
              supplierId: supplier,
              searchValue: searchInput,
              nextPage: nextPageUrl,
            },
            false
          );
        }}
        hasMore={hasMore}
        threshold={INFINITE_SCROLL_THRESHOLD_PX}
        loader={
          <div key="notifications-loader" className="text-center py-2">
            <Spinner animation="border" size="sm" role="status" />
            <div className="mt-1">
              <small className="text-muted">Loading more notifications...</small>
            </div>
          </div>
        }
      >
        <div className="glass-table-wrap">
        <NotificationsTable
          notificationsList={notifications}
          handleEdit={refreshList}
          onDeleteSuccess={handleDeleteSuccess}
          clearSearchValue={resetSearchValue}
        />
        </div>
      </InfiniteScroll>
      )}

      <Modal
        show={showClearAllModal}
        onHide={() => setShowClearAllModal(false)}
        dialogClassName="procure-modal-glass procure-modal-glass-form"
      >
        <Modal.Header closeButton className="procure-modal__header">
          <Modal.Title>Clear all reorder alerts</Modal.Title>
        </Modal.Header>
        <Modal.Body className="procure-modal__body">
          Are you sure you want to clear all reorder alerts? This cannot be undone.
        </Modal.Body>
        <Modal.Footer className="procure-modal__footer">
          {isClearingAll ? (
            <Button variant="danger" disabled>
              <Spinner size="sm" animation="border" role="status" aria-hidden="true" />
            </Button>
          ) : (
            <>
              <Button variant="danger" onClick={handleClearAll}>
                Yes, clear all
              </Button>
              <Button
                variant="secondary"
                className="procure-modal__close-btn"
                onClick={() => setShowClearAllModal(false)}
              >
                Cancel
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <GlobalOrderContactModal
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        token={token}
        onSettingsUpdated={settings => {
          setContactSettings(settings);
          logInfo(LOG_CONTEXT, 'contact settings updated from notifications page', {
            hasEmail: Boolean(settings?.contact_email),
          });
        }}
      />
    </div>
  );
};

export default NotificationsList;
